import { formatDistanceToNow, fromUnixTime } from 'date-fns';
import { useGetClusterNodes } from 'api/frontend';
import MessageBox from 'components/MessageBox';
import Centered from 'components/Centered';
import useUserAccountState from 'hooks/useUserAccountState';
import { Badge, SkeletonText, Table } from 'subframe/index';
import { formatKubernetesAge } from 'utils/formatTime';

const COLUMNS = [
  'Name',
  'Status',
  'Roles',
  'Age',
  'Version',
  'Internal IP',
  'External IP',
  'OS Image',
  'Kernel Version',
  'Container Runtime',
] as const;

const SKELETON_ROW_COUNT = 3;

const CELL_TEXT_CLASS =
  'min-w-0 truncate text-body font-body text-default-font';

function getStatusBadgeVariant(
  status: string,
): 'success' | 'error' | 'neutral' {
  switch (status) {
    case 'Ready':
      return 'success';
    case 'NotReady':
      return 'error';
    default:
      return 'neutral';
  }
}

function formatNodeAge(created: number): string {
  return formatKubernetesAge(created);
}

function formatExternalIp(externalIp?: string | null): string {
  return externalIp?.trim() ? externalIp : '-';
}

type ClusterNodesTableProps = {
  clusterId: string;
};

export default function ClusterNodesTable({
  clusterId,
}: ClusterNodesTableProps) {
  const { account } = useUserAccountState();

  const { data, isLoading, error } = useGetClusterNodes(clusterId, {
    request: { headers: { authorization: `Bearer ${account?.token}` } },
    swr: { enabled: !!clusterId && !!account?.token },
  });

  const nodes = data?.nodes ?? [];
  const lastUpdatedText =
    data?.updated != null
      ? formatDistanceToNow(fromUnixTime(data.updated), { addSuffix: true })
      : null;

  return (
    <div className="flex w-full flex-col gap-2">
      <div className="flex flex-col gap-1 px-2">
        <span className="text-subheader font-subheader text-default-font">
          Nodes
        </span>
        {lastUpdatedText && (
          <span className="text-body font-body text-subtext-color">
            Last updated {lastUpdatedText}
          </span>
        )}
      </div>

      {!isLoading && error ? (
        <Centered>
          <MessageBox mode="error" message="Error fetching nodes" />
        </Centered>
      ) : !isLoading && !error && nodes.length === 0 ? (
        <Centered>
          <span className="text-body font-body text-subtext-color">
            Node information is not available yet. Re-scan the cluster to view
            latest data.
          </span>
        </Centered>
      ) : (
        <div className="flex w-full">
          <Table
            header={
              <Table.HeaderRow>
                {COLUMNS.map((column) => (
                  <Table.HeaderCell key={column}>{column}</Table.HeaderCell>
                ))}
              </Table.HeaderRow>
            }
          >
            {isLoading &&
              Array.from({ length: SKELETON_ROW_COUNT }).map((_, rowIndex) => (
                <Table.Row key={`skeleton-${rowIndex}`}>
                  {COLUMNS.map((column) => (
                    <Table.Cell key={`${rowIndex}-${column}`}>
                      <SkeletonText className="h-5 w-full" />
                    </Table.Cell>
                  ))}
                </Table.Row>
              ))}

            {!isLoading &&
              !error &&
              nodes.map((node) => (
                <Table.Row key={node.name}>
                  <Table.Cell className="min-w-0">
                    <span className={CELL_TEXT_CLASS} title={node.name}>
                      {node.name}
                    </span>
                  </Table.Cell>
                  <Table.Cell className="min-w-0">
                    <Badge variant={getStatusBadgeVariant(node.status)}>
                      {node.status}
                    </Badge>
                  </Table.Cell>
                  <Table.Cell className="min-w-0">
                    <span
                      className={CELL_TEXT_CLASS}
                      title={(node.roles ?? []).join(', ')}
                    >
                      {(node.roles ?? []).join(', ') || '-'}
                    </span>
                  </Table.Cell>
                  <Table.Cell className="min-w-0">
                    <span className={CELL_TEXT_CLASS}>
                      {formatNodeAge(node.created)}
                    </span>
                  </Table.Cell>
                  <Table.Cell className="min-w-0">
                    <span className={CELL_TEXT_CLASS} title={node.version}>
                      {node.version}
                    </span>
                  </Table.Cell>
                  <Table.Cell className="min-w-0">
                    <span className={CELL_TEXT_CLASS} title={node.internal_ip}>
                      {node.internal_ip}
                    </span>
                  </Table.Cell>
                  <Table.Cell className="min-w-0">
                    <span
                      className={CELL_TEXT_CLASS}
                      title={formatExternalIp(node.external_ip)}
                    >
                      {formatExternalIp(node.external_ip)}
                    </span>
                  </Table.Cell>
                  <Table.Cell className="min-w-0">
                    <span className={CELL_TEXT_CLASS} title={node.os_image}>
                      {node.os_image}
                    </span>
                  </Table.Cell>
                  <Table.Cell className="min-w-0">
                    <span
                      className={CELL_TEXT_CLASS}
                      title={node.kernel_version}
                    >
                      {node.kernel_version}
                    </span>
                  </Table.Cell>
                  <Table.Cell className="min-w-0">
                    <span
                      className={CELL_TEXT_CLASS}
                      title={node.container_runtime}
                    >
                      {node.container_runtime}
                    </span>
                  </Table.Cell>
                </Table.Row>
              ))}
          </Table>
        </div>
      )}
    </div>
  );
}
